import React, { useState } from "react";
import { API_URL } from "../../config";

const CompanyEditForm = ({ company, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    name: company.name ?? "",
    email: company.email ?? "",
    phone: company.phone ?? "",
    address: company.address ?? "",
    is_active: Number(company.is_active) === 1,
    auto_archive_enabled: Number(company.auto_archive_enabled) === 1,
    archive_after_months: String(company.archive_after_months),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const change = (field, value) => {
    setForm(previous => ({ ...previous, [field]: value }));
  };

  const save = async event => {
    event.preventDefault();
    if (saving) return;
    setError("");

    const months = Number(form.archive_after_months);
    if (!Number.isInteger(months) || months < 1 || months > 120) {
      setError("Die Archivierungsfrist muss zwischen 1 und 120 Monaten liegen.");
      return;
    }

    if (
      Number(company.is_active) === 1 &&
      !form.is_active &&
      !window.confirm(
        `„${company.name}“ deaktivieren? Alle Firmenkonten verlieren den Zugriff.`
      )
    ) {
      return;
    }

    const body = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
      is_active: form.is_active,
      auto_archive_enabled: form.auto_archive_enabled,
      archive_after_months: months,
    };

    setSaving(true);

    try {
      const response = await fetch(
        `${API_URL}/api/companies/${company.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || "Unternehmen konnte nicht gespeichert werden.");
      }

      onSaved({
        ...company,
        ...body,
        is_active: Number(body.is_active),
        auto_archive_enabled: Number(body.auto_archive_enabled),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-panel company-settings">
      <h3>Unternehmen bearbeiten: {company.name}</h3>

      <form onSubmit={save}>
        <fieldset disabled={saving}>
          <legend>Unternehmensdaten</legend>

          <label htmlFor="edit-company-name">Unternehmensname *</label>
          <input
            id="edit-company-name"
            value={form.name}
            onChange={event => change("name", event.target.value)}
            required
            maxLength={255}
          />

          <label htmlFor="edit-company-email">E-Mail</label>
          <input
            id="edit-company-email"
            type="email"
            value={form.email}
            onChange={event => change("email", event.target.value)}
            maxLength={255}
          />

          <label htmlFor="edit-company-phone">Telefon</label>
          <input
            id="edit-company-phone"
            type="tel"
            value={form.phone}
            onChange={event => change("phone", event.target.value)}
            maxLength={50}
          />

          <label htmlFor="edit-company-address">Adresse</label>
          <textarea
            id="edit-company-address"
            value={form.address}
            onChange={event => change("address", event.target.value)}
            maxLength={500}
            rows={3}
          />
        </fieldset>

        <fieldset disabled={saving}>
          <legend>Status und Archivierung</legend>

          <label>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={event => change("is_active", event.target.checked)}
            />
            Unternehmen aktiv
          </label>

          <label>
            <input
              type="checkbox"
              checked={form.auto_archive_enabled}
              onChange={event =>
                change("auto_archive_enabled", event.target.checked)
              }
            />
            Automatische Archivierung aktiv
          </label>

          <label htmlFor="edit-company-months">
            Archivierungsfrist ab Buchungsdatum in Monaten
          </label>
          <input
            id="edit-company-months"
            type="number"
            min={1}
            max={120}
            step={1}
            value={form.archive_after_months}
            onChange={event =>
              change("archive_after_months", event.target.value)
            }
            required
          />

          <p>
            Eine kürzere Frist kann gebuchte Abschlüsse beim nächsten
            stündlichen Durchlauf archivieren. Archivierte Einträge
            bleiben gesperrt.
          </p>
        </fieldset>

        {error && <p role="alert">{error}</p>}

        <button type="submit" disabled={saving}>
          {saving ? "Wird gespeichert …" : "Änderungen speichern"}
        </button>
        <button type="button" disabled={saving} onClick={onCancel}>
          Abbrechen
        </button>
      </form>
    </section>
  );
};

export default CompanyEditForm;